import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  getShipmentTrack,
  getShipmentTrackingEvents,
  type TrackingEvent,
} from '@/services/api/shipments'
import { formatDcsaEventLabel } from '@/utils/dcsaEventLabels'

interface ShipmentStatusModalProps {
  isOpen: boolean
  onClose: () => void
  shipmentData?: {
    id?: string
    booking?: string
    billOfLading: string
    containerNumber: string
    vessel: string
    shippingLine: string
    product: string
    customer: string
    origin: string
    destination: string
    etd: string
    eta: string
    status: string
  }
}

function formatEventDateTime(iso?: string): { date: string; time: string } {
  if (!iso) return { date: '—', time: '' }
  try {
    const d = new Date(iso)
    const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
    return { date, time }
  } catch {
    return { date: '—', time: '' }
  }
}

function toTimelineItem(
  e: {
    type?: string
    eventType?: string
    description?: string
    location?: string
    dateTime?: string
    eventDateTime?: string
  },
  index: number,
  total: number
) {
  const dt = e.dateTime ?? e.eventDateTime
  const { date, time } = formatEventDateTime(dt)
  const code = e.type ?? e.eventType
  const title = formatDcsaEventLabel(e.description?.trim() || code)
  const sub = [e.location].filter(Boolean).join(' ')
  const isLatest = index === total - 1
  return { date, time, title, status: sub || title, isActive: isLatest }
}

const ShipmentStatusModal = ({ isOpen, onClose, shipmentData }: ShipmentStatusModalProps) => {
  const [events, setEvents] = useState<TrackingEvent[]>([])
  const [liveStatus, setLiveStatus] = useState<string | null>(null)
  const [liveEta, setLiveEta] = useState<string | null>(null)
  const [liveEtd, setLiveEtd] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !shipmentData?.id) {
      setEvents([])
      setLiveStatus(null)
      setLiveEta(null)
      setLiveEtd(null)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    const id = shipmentData.id

    const fetchTracking = async () => {
      const finished = /^(arrived|fully_delivered|cancelled)$/i.test(shipmentData.status || '')
      try {
        const stored = await getShipmentTrackingEvents(id)
        const storedEvents = stored.events ?? []
        if (storedEvents.length > 0) {
          const sorted = [...storedEvents].sort((a, b) => {
            const da = a.eventDateTime ? new Date(a.eventDateTime).getTime() : 0
            const db = b.eventDateTime ? new Date(b.eventDateTime).getTime() : 0
            return da - db
          })
          setEvents(sorted)
          return
        }

        if (finished) return

        const refs: Array<'bl' | 'container' | 'booking'> = shipmentData.booking
          ? ['booking', 'bl', 'container']
          : ['bl', 'container', 'booking']
        for (const by of refs) {
          try {
            const track = await getShipmentTrack(id, by)
            const evs = track.events ?? []
            if (track.status) setLiveStatus(track.status)
            if (track.eta) setLiveEta(track.eta)
            if (track.etd) setLiveEtd(track.etd)
            if (evs.length > 0) {
              const sorted = [...evs].sort((a, b) => {
                const da = a.dateTime ? new Date(a.dateTime).getTime() : 0
                const db = b.dateTime ? new Date(b.dateTime).getTime() : 0
                return da - db
              })
              setEvents(
                sorted.map((e) => ({
                  eventType: e.type ?? e.eventType,
                  description: (e as { description?: string }).description,
                  location: e.location,
                  eventDateTime: e.dateTime,
                }))
              )
              return
            }
          } catch {
            // Try next ref
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load tracking')
      } finally {
        setLoading(false)
      }
    }

    fetchTracking()
  }, [isOpen, shipmentData?.id, shipmentData?.status, shipmentData?.booking])

  if (!isOpen || !shipmentData) return null

  const displayStatus = liveStatus ?? shipmentData.status
  const displayEta = liveEta ?? shipmentData.eta
  const displayEtd = liveEtd ?? shipmentData.etd
  const statusPill = (
    <span
      className="inline-flex items-center justify-center rounded px-2 py-1"
      style={{
        backgroundColor: '#EAF1FF',
        color: '#2C71F6',
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '18px'
      }}
    >
      {displayStatus || '—'}
    </span>
  )

  const details = [
    { label: 'Booking', value: shipmentData.booking || '-' },
    { label: 'Bill of Lading', value: shipmentData.billOfLading || '-' },
    { label: 'Container Number', value: shipmentData.containerNumber || '-' },
    { label: 'Vessel', value: shipmentData.vessel || '-' },
    { label: 'Shipping Line', value: shipmentData.shippingLine || '-' },
    { label: 'Product', value: shipmentData.product || '-' },
    { label: 'Customer', value: shipmentData.customer || '-' },
    { label: 'Origin', value: shipmentData.origin || '-' },
    { label: 'Destination', value: shipmentData.destination || '-' },
    { label: 'ETD', value: displayEtd || '-' },
    { label: 'ETA', value: displayEta || shipmentData.eta || '-' },
    { label: 'Status', value: statusPill }
  ]

  const timelineItems = events.map((e, i) =>
    toTimelineItem(
      {
        type: e.eventType,
        eventType: e.eventType,
        description: e.description,
        location: e.location,
        dateTime: e.eventDateTime,
        eventDateTime: e.eventDateTime,
      },
      i,
      events.length
    )
  )

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
      onClick={onClose}
      style={{ zIndex: 60 }}
    >
      <div
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '420px',
          maxWidth: '420px',
          height: '100vh',
          maxHeight: '100vh',
          zIndex: 70,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          overflow: 'hidden'
        }}
      >
        <div
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0 px-4 sm:px-6"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2'
          }}
        >
          <h2
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '18px',
              lineHeight: '26px',
              letterSpacing: '0px',
              color: '#212126'
            }}
          >
            Shipment Status
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{
              width: '24px',
              height: '24px'
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="10.5"
                stroke="#212126"
                strokeWidth="1.5"
                fill="none"
              />
              <line
                x1="9.17"
                y1="9.17"
                x2="14.83"
                y2="14.83"
                stroke="#212126"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <line
                x1="14.83"
                y1="9.17"
                x2="9.17"
                y2="14.83"
                stroke="#212126"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4" style={{ minHeight: 0 }}>
          <div className="space-y-0">
            {details.map((row) => (
              <div key={row.label} className="flex items-center justify-between py-3 border-b border-gray-200">
                <span
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 300,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0px',
                    color: '#69697A'
                  }}
                >
                  {row.label}
                </span>
                <span
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0px',
                    color: '#212126'
                  }}
                >
                  {row.value}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-6 space-y-0">
            {loading && (
              <div className="py-6 text-center" style={{ color: '#69697A', fontSize: '13px' }}>
                Loading tracking data…
              </div>
            )}
            {error && !loading && (
              <div className="py-6 text-center" style={{ color: '#B91C1C', fontSize: '13px' }}>
                {error}
              </div>
            )}
            {!loading && !error && timelineItems.length === 0 && (
              <div className="py-6 text-center" style={{ color: '#69697A', fontSize: '13px' }}>
                {/^(arrived|fully_delivered|cancelled)$/i.test(shipmentData.status || '')
                  ? 'No tracking history stored for this booking.'
                  : 'No tracking events yet. They appear after the first carrier update.'}
              </div>
            )}
            {!loading && timelineItems.map((item, index) => {
              const isActive = Boolean(item.isActive)
              const indicatorColor = isActive ? '#2C71F6' : '#C7CCD5'
              const textColor = isActive ? '#212126' : '#69697A'
              const isLast = index === timelineItems.length - 1

              return (
                <div key={`${item.date}-${item.time}-${item.title}-${index}`} className="flex gap-3">
                  <div
                    className="text-right flex-shrink-0"
                    style={{ width: '70px', color: '#69697A', fontSize: '11px', lineHeight: '16px' }}
                  >
                    <div>{item.date}</div>
                    <div>{item.time}</div>
                  </div>
                  <div className="relative flex flex-col items-center" style={{ width: '20px' }}>
                    <div
                      className="absolute"
                      style={{
                        top: 0,
                        bottom: isLast ? '12px' : 0,
                        width: '1px',
                        backgroundColor: '#E5E7EB'
                      }}
                    />
                    <div
                      className="relative z-10 flex items-center justify-center rounded-full"
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#FFFFFF',
                        border: `1px solid ${indicatorColor}`
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <polyline
                          points="2.5,6.5 5,9 9.5,3.5"
                          stroke={indicatorColor}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="pb-6 flex-1">
                    <div
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 500,
                        fontSize: '12px',
                        lineHeight: '18px',
                        color: textColor
                      }}
                    >
                      {item.title}
                    </div>
                    <div
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '11px',
                        lineHeight: '16px',
                        color: textColor
                      }}
                    >
                      {item.status}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div
          className="flex items-center justify-end flex-shrink-0 border-t border-gray-200 px-4 sm:px-6"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderBottomLeftRadius: '12px'
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#212126',
              border: '1px solid #212126',
              backgroundColor: '#FFFFFF',
              height: '38px'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default ShipmentStatusModal
